import React, { Fragment, useEffect, useState } from 'react'
import { Box, Container, Divider, IconButton, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { rightPanelStyles } from '../../styles/classes/FormBuilderClasses'
import TextBox from '../general/TextBox'
import CustomSelect from '../general/CustomSelect'
import {
  cloneDeep,
  getFirstKeyFromObject,
  elementKeys,
  elementTypes,
  inputFieldTypeOptions,
  selectionOptionOptions,
  selectionTypeOptions,
  thenPerformOptions,
  maximumNumberOfPhotos,
  photoObjectTemplate,
  attributesOptions,
  attachmentsAttributesOptions,
  photosItemKeys,
  maximumNumberOfButtons,
  checkNotFullfilled,
  signatureAttributesOptions,
  itemPriceOptions,
  lineItemsLogicalActionOptions,
  attachmentsItemKeys,
  attachmentsObjectTemplate,
  maximumNumberOfAttachments,
  rateOptions,
  requiredAuthorizationOptions,
  paymentTermsOptions,
  materialsLogicalActionOptions,
  rateOptionsObj,
  lineItemsFieldsOptions
} from '../../utils/formBuilder'

import { faClone, faTrash } from '@fortawesome/pro-thin-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import SelectionComponent from '../general/SelectionComponent'
import PhotosListComponent from '../general/PhotosListComponent'
import ButtonsListComponent from '../general/ButtonsListComponent'
import { statusOptions } from '../../utils/fieldForms'
import AttachmentsListComponent from '../general/AttachmentsListComponent'

const RightPanel = ({
  emptySections,
  selectedItem,
  setSelectedItem,
  changes,
  setChanges,
  mandatoryFields,
  selectedObjectCounter,
  onNestedLogicTrue,
  onNestedLogicFalse,
  onCloneElement,
  onDeleteElement,
  status,
  onUpdateNestedLogic,
  extraConditions
}) => {
  const selectedItemKey = getFirstKeyFromObject(selectedItem)
  const selectedItemType = selectedItem?.[selectedItemKey]?.type
  const [selectedMoreThanOne, setSelectedMoreThanOne] = useState(false)
  const [selectionOptions, setSelectionOptions] = useState(
    selectedItem?.[selectedItemKey]?.options ?? ['']
  )
  const [photosList, setPhotosList] = useState(
    selectedItem?.[selectedItemKey]?.photos ?? []
  )
  const [numberOfPhotos, setNumberOfPhotos] = useState(
    selectedItem?.[selectedItemKey]?.number_of_photos ?? 1
  )

  const [attachmentsList, setAttachmentsList] = useState(
    selectedItem?.[selectedItemKey]?.attachments ?? []
  )
  const [numberOfAttachments, setNumberOfAttachments] = useState(
    selectedItem?.[selectedItemKey]?.number_of_attachments ?? 1
  )
  const [buttonsList, setButtonsList] = useState([''])
  const [numberOfButtons, setNumberOfButtons] = useState(
    selectedItem?.[selectedItemKey]?.number_of_buttons ?? 1
  )
  const [missingValuesCounter, setMissingValuesCounter] = useState(0)
  const { t } = useTranslation()
  const { classes } = rightPanelStyles()
  const disableItems = statusOptions.PUBLISHED === status

  useEffect(() => {
    if (
      selectedItem &&
      (selectedItemType === elementTypes.DROPDOWN_SELECTION ||
        selectedItemType === elementTypes.LINE_ITEMS ||
        selectedItemType === elementTypes.SERVICE_CHECKLIST)
    ) {
      setSelectionOptions(selectedItem[selectedItemKey]?.options ?? [])
    }
    if (selectedItem && selectedItemType === elementTypes.PHOTOS) {
      setPhotosList(selectedItem[selectedItemKey]?.photos ?? [])
      setNumberOfPhotos(selectedItem[selectedItemKey]?.number_of_photos ?? 1)
    }
    if (selectedItem && selectedItemType === elementTypes.ATTACHMENTS) {
      setAttachmentsList(selectedItem[selectedItemKey]?.attachments ?? [])
      setNumberOfAttachments(
        selectedItem[selectedItemKey]?.number_of_attachments ?? 1
      )
    }
    if (
      selectedItem &&
      selectedItemType === elementTypes.MULTI_BUTTON_SELECTION
    ) {
      setButtonsList(selectedItem[selectedItemKey]?.buttons ?? [''])
      setNumberOfButtons(selectedItem[selectedItemKey]?.number_of_buttons ?? 1)
    }
    if (selectedItem) {
      const timeoutId = setTimeout(checkMissing, 500)
      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [selectedItem])

  useEffect(() => {
    let count = 0
    if (selectedObjectCounter[getFirstKeyFromObject(selectedItem)]) {
      count = selectedObjectCounter[getFirstKeyFromObject(selectedItem)]
    }
    setSelectedMoreThanOne(count > 1)
  }, [selectedObjectCounter])

  // clone enabled or disabled
  // based on this
  const checkMissing = () => {
    let missingFieldsCount = 0
    const questionObj = selectedItem[selectedItemKey]
    for (const field of mandatoryFields) {
      // check if question has key field
      if (Object.prototype.hasOwnProperty.call(questionObj, field)) {
        const notFullfilled = checkNotFullfilled(questionObj, field)
        if (notFullfilled) missingFieldsCount++
      }
    }
    setMissingValuesCounter(missingFieldsCount)
  }

  const emptyMessage = () => {
    return (
      <Box className={classes.emptyMessageContainer}>
        <Box>
          <Typography className={classes.emptyStateTitle}>
            {t('form_builder.right_panel.add_element')}
          </Typography>
          <Typography className={classes.emptyMessageText}>
            {t('form_builder.right_panel.fields_reorder')}
          </Typography>
        </Box>
      </Box>
    )
  }

  const continueMessage = () => {
    return (
      <Box className={classes.continueMessageContainer}>
        <Typography align="center" className={classes.continueMessageTitle}>
          {t('form_builder.right_panel.continue_message_title')}
        </Typography>
        <Typography align="center" className={classes.continueMessageText}>
          {t('form_builder.right_panel.continue_message_text')}
        </Typography>
      </Box>
    )
  }

  useEffect(() => {
    if (selectedItem?.[selectedItemKey]?.product_creation === true) {
      const newObject = cloneDeep(selectedItem)
      newObject[selectedItemKey][elementKeys.PRICE_EACH] = 'editable'
      newObject[selectedItemKey][elementKeys.PRODUCT_TYPE] = 'editable'
      setSelectedItem(newObject)
    }
  }, [selectedItem?.[selectedItemKey]?.product_creation])

  const changeFieldHandler = event => {
    if (!changes) setChanges(true)
    const newObject = cloneDeep(selectedItem)
    switch (event.target.name) {
      case elementKeys.REQUIRED: {
        switch (selectedItem[selectedItemKey]?.type) {
          case elementTypes.AUTHORIZATION_FIELD:
            selectedListOptionHandler(
              event,
              requiredAuthorizationOptions,
              newObject
            )
            setSelectedItem(newObject)
            break
          default: {
            const trueLabel = t('form_builder.right_panel.required')
            const trueLabelAlt =
              trueLabel + ' ' + `(${t('form_builder.elements.one_line_item')})`
            if (
              event.target.value === trueLabel ||
              event.target.value === trueLabelAlt
            ) {
              newObject[selectedItemKey][event.target.name] = true
            } else {
              newObject[selectedItemKey][event.target.name] = false
            }
            setSelectedItem(newObject)
            break
          }
        }
        break
      }
      case elementKeys.PRODUCT_CREATION:
        trueFalseEnableDisableHandler(event, newObject)
        setSelectedItem(newObject)
        break
      case elementKeys.PRODUCT_NAME:
      case elementKeys.PRODUCT_TYPE:
      case elementKeys.PRODUCT_DESCRIPTION:
      case elementKeys.PRICE_EACH:
      case elementKeys.TAX:
      case elementKeys.TOTAL_EACH:
        selectedListOptionHandler(event, lineItemsFieldsOptions, newObject)
        setSelectedItem(newObject)
        break
      case elementKeys.PAYMENT_TERMS:
        selectedListOptionHandler(event, paymentTermsOptions, newObject)
        setSelectedItem(newObject)
        break
      case elementKeys.DESCRIPTION:
      case elementKeys.TOTAL_AMOUNT_DUE:
      case elementKeys.LABOR_TYPE:
      case elementKeys.AUTHORIZATION_TO_PROCEED:
      case elementKeys.RATE:
        selectedListOptionHandler(event, rateOptions, newObject)
        setSelectedItem(newObject)
        break
      case elementKeys.INPUT_FIELD_TYPE:
        selectedListOptionHandler(event, inputFieldTypeOptions, newObject)
        setSelectedItem(newObject)
        break
      case elementKeys.ATTRIBUTES:
        selectedListOptionHandler(event, signatureAttributesOptions, newObject)
        setSelectedItem(newObject)
        break
      case elementKeys.SELECTION_TYPE:
        selectedListOptionHandler(event, selectionTypeOptions, newObject)
        setSelectedItem(newObject)
        break
      case elementKeys.NUMBER_OF_SELECTIONS:
        selectedListOptionHandler(event, selectionTypeOptions, newObject)
        setSelectedItem(newObject)
        break
      case elementKeys.SELECTION_OPTION:
        selectedListOptionHandler(event, selectionOptionOptions, newObject)
        setSelectedItem(newObject)
        break
      case elementKeys.ITEM_PRICE:
        selectedListOptionHandler(event, itemPriceOptions, newObject)
        setSelectedItem(newObject)
        break
      case elementKeys.TIME_STAMP:
        trueFalseYesNoHandler(event, newObject)
        setSelectedItem(newObject)
        break
      case elementKeys.NESTED_LOGIC: {
        trueFalseYesNoHandler(event, newObject)
        if (newObject[selectedItemKey].nested_logic === false) {
          onNestedLogicFalse(selectedItemKey)
          newObject[selectedItemKey][elementKeys.THEN_PERFORM] = '' // resets logical action
        }
        setSelectedItem(newObject)
        break
      }
      case elementKeys.NUMBER_OF_BUTTONS:
        newObject[selectedItemKey][event.target.name] = event.target.value
        setNumberOfButtons(event.target.value)
        setSelectedItem(newObject)
        break
      case elementKeys.LOGICAL_ACTION:
        switch (selectedItem[selectedItemKey]?.type) {
          case elementTypes.MULTI_BUTTON_SELECTION:
          case elementTypes.DROPDOWN_SELECTION: {
            const thenum = event.target.value.replace(/\D/g, '')
            newObject[selectedItemKey][event.target.name] = thenum
            setSelectedItem(newObject)
            onUpdateNestedLogic(selectedItemKey, thenum)
            break
          }
          case elementTypes.CHECKLIST_ITEM: {
            let valueToBeSaved = false
            if (
              event.target.value ===
              t('form_builder.elements.if_check_mark_is_selected')
            ) {
              valueToBeSaved = true
            }
            newObject[selectedItemKey][event.target.name] = valueToBeSaved
            setSelectedItem(newObject)
            onUpdateNestedLogic(selectedItemKey, valueToBeSaved)
            break
          }
          case elementTypes.LINE_ITEMS: {
            let valueToBeSaved = null
            switch (event.target.value) {
              case t('form_builder.elements.if_one_line_item_is_populated'):
                valueToBeSaved = 'if_one_line_item_is_populated'
                break
              case t(
                'form_builder.elements.if_more_than_one_line_item_is_populated'
              ):
                valueToBeSaved = 'if_more_than_one_line_item_is_populated'
                break
            }
            newObject[selectedItemKey][event.target.name] = valueToBeSaved
            setSelectedItem(newObject)
            onUpdateNestedLogic(selectedItemKey, valueToBeSaved)
            break
          }
          case elementTypes.MATERIAL_PARTS: {
            let valueToBeSaved = null
            switch (event.target.value) {
              case t('form_builder.elements.if_one_material_is_populated'):
                valueToBeSaved = 'if_one_material_is_populated'
                break
              case t(
                'form_builder.elements.if_more_than_one_material_is_populated'
              ):
                valueToBeSaved = 'if_more_than_one_material_is_populated'
                break
            }
            newObject[selectedItemKey][event.target.name] = valueToBeSaved
            setSelectedItem(newObject)
            onUpdateNestedLogic(selectedItemKey, valueToBeSaved)
            break
          }
          default:
            newObject[selectedItemKey][event.target.name] = event.target.value
            setSelectedItem(newObject)
            onUpdateNestedLogic(selectedItemKey, event.target.value)
            break
        }
        break
      case elementKeys.THEN_PERFORM:
        thenPerformChangeHandler(event)
        break
      case elementKeys.NUMBER_OF_PHOTOS:
        setNumberOfPhotos(event.target.value)
        newObject[selectedItemKey][event.target.name] = event.target.value
        setSelectedItem(newObject)
        break
      case elementKeys.NUMBER_OF_ATTACHMENTS:
        setNumberOfAttachments(event.target.value)
        newObject[selectedItemKey][event.target.name] = event.target.value
        setSelectedItem(newObject)
        break
      default:
        newObject[selectedItemKey][event.target.name] = event.target.value
        setSelectedItem(newObject)
        break
    }
  }

  const selectedListOptionHandler = (event, list, newObject) => {
    list.forEach(option => {
      if (event.target.value === t(`form_builder.elements.${option}`)) {
        newObject[selectedItemKey][event.target.name] = option
      }
    })
  }

  const trueFalseEnableDisableHandler = (event, newObject) => {
    if (event.target.value === t('form_builder.elements.enable')) {
      newObject[selectedItemKey][event.target.name] = true
    } else {
      newObject[selectedItemKey][event.target.name] = false
    }
  }

  const trueFalseYesNoHandler = (event, newObject) => {
    if (event.target.value === t('form_builder.elements.yes')) {
      newObject[selectedItemKey][event.target.name] = true
    } else {
      newObject[selectedItemKey][event.target.name] = false
    }
    setSelectedItem(newObject)
    // remove element if nested logic false
    if (newObject[selectedItemKey].nested_logic === false) {
      onNestedLogicFalse(selectedItemKey)
    }
  }

  const thenPerformChangeHandler = event => {
    const newObject = cloneDeep(selectedItem)
    // action when then perform

    thenPerformOptions.forEach(option => {
      if (event.target.value === t(`form_builder.elements.${option}`)) {
        newObject[selectedItemKey][event.target.name] = option
      }
    })
    setSelectedItem(newObject)
    if (
      newObject[selectedItemKey].nested_logic === true &&
      newObject[selectedItemKey].then_perform &&
      newObject[selectedItemKey].then_perform !== ''
    ) {
      // remove if existing
      onNestedLogicFalse(selectedItemKey)
      // add new nested value
      onNestedLogicTrue(
        newObject[selectedItemKey].then_perform,
        selectedItemKey,
        newObject[selectedItemKey].logical_action
      )
    }
  }

  const clearFieldHandler = name => {
    if (!changes) setChanges(true)
    const newObject = cloneDeep(selectedItem)
    newObject[selectedItemKey][name] = ''
    setSelectedItem(newObject)
  }

  // Drop Down Selection actions start
  useEffect(() => {
    // if selection options are updated
    // use debouncing so proces for updating the whole object
    // only executes once if multiple keystrokes
    if (selectionOptions) {
      const timeoutId = setTimeout(updateOptions, 500)
      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [selectionOptions])

  const updateOptions = () => {
    if (selectedItem) {
      if (!changes) setChanges(true)
      const newObject = cloneDeep(selectedItem)
      newObject[selectedItemKey].options = selectionOptions
      setSelectedItem(newObject)
    }
  }

  const addSelectionHandler = () => {
    setSelectionOptions(prevValues => [...prevValues, ''])
  }

  const removeSelectionHandler = index => {
    setSelectionOptions(prevValues => prevValues.filter((_, i) => i !== index))
  }

  const changeOptionTextHandler = (event, index) => {
    const newArray = cloneDeep(selectionOptions)
    newArray[index] = event.target.value
    setSelectionOptions(newArray)
  }
  // Drop Down Selection actions end

  // Photos element actions start
  const changePhotosListItemHandler = (event, index) => {
    if (!changes) setChanges(true)
    const eventName = event.target.name
    const newArray = cloneDeep(photosList)
    if (
      eventName === photosItemKeys.LOCATION ||
      eventName === photosItemKeys.TIME_STAMP
    ) {
      if (event.target.value === t('form_builder.elements.yes')) {
        newArray[index] = { ...newArray[index], [eventName]: true }
      } else {
        newArray[index] = { ...newArray[index], [eventName]: false }
      }
    }

    if (eventName === photosItemKeys.REQUIRED) {
      if (event.target.value === t('form_builder.right_panel.required')) {
        newArray[index] = { ...newArray[index], [eventName]: true }
      } else {
        newArray[index] = { ...newArray[index], [eventName]: false }
      }
    }

    if (eventName === photosItemKeys.ATTRIBUTES) {
      attributesOptions.forEach(option => {
        if (event.target.value === t(`form_builder.elements.${option}`)) {
          newArray[index] = { ...newArray[index], [eventName]: option }
        }
      })
    }

    if (eventName === photosItemKeys.TITLE) {
      newArray[index] = { ...newArray[index], [eventName]: event.target.value }
    }
    setPhotosList(newArray)
  }
  const clearPhotoTitleHandler = index => {
    const newArray = cloneDeep(photosList)
    newArray[index] = { ...newArray[index], title: '' }
    setPhotosList(newArray)
  }
  // if changes in photos number_of_photos
  // update photosList
  useEffect(() => {
    if (photosList.length === numberOfPhotos) return
    // remove extra items
    if (photosList.length > numberOfPhotos) {
      setPhotosList(prevList => {
        return prevList.slice(0, numberOfPhotos)
      })
      return
    }
    // add extra items
    if (photosList.length < numberOfPhotos) {
      setPhotosList(prevList => {
        while (prevList.length < numberOfPhotos) {
          prevList.push(photoObjectTemplate)
        }
        return prevList
      })
    }
  }, [numberOfPhotos])

  useEffect(() => {
    // if photos list is updated
    // use debouncing so proces for updating the whole object
    // only executes once if multiple keystrokes
    if (photosList?.length && changes) {
      const timeoutId = setTimeout(updatePhotoListInSelectedItem, 500)
      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [photosList])

  const updatePhotoListInSelectedItem = () => {
    if (selectedItem) {
      const newObject = cloneDeep(selectedItem)
      newObject[selectedItemKey].photos = photosList
      let required = false
      photosList.forEach(photo => {
        if (photo.required) {
          required = true
        }
      })
      newObject[selectedItemKey].required = required
      setSelectedItem(newObject)
    }
  }
  // Photos element actions end

  // Attachments element actions start
  const changeAttachmentsListItemHandler = (event, index) => {
    if (!changes) setChanges(true)
    const eventName = event.target.name
    const newArray = cloneDeep(attachmentsList)
    if (
      eventName === attachmentsItemKeys.LOCATION ||
      eventName === attachmentsItemKeys.TIME_STAMP
    ) {
      if (event.target.value === t('form_builder.elements.yes')) {
        newArray[index] = { ...newArray[index], [eventName]: true }
      } else {
        newArray[index] = { ...newArray[index], [eventName]: false }
      }
    }

    if (eventName === attachmentsItemKeys.ATTRIBUTES) {
      attachmentsAttributesOptions.forEach(option => {
        if (event.target.value === t(`form_builder.elements.${option}`)) {
          newArray[index] = { ...newArray[index], [eventName]: option }
        }
      })
    }

    if (eventName === attachmentsItemKeys.TITLE) {
      newArray[index] = { ...newArray[index], [eventName]: event.target.value }
    }
    if (eventName === attachmentsItemKeys.REQUIRED) {
      if (event.target.value === t('form_builder.right_panel.required')) {
        newArray[index] = { ...newArray[index], [eventName]: true }
      } else {
        newArray[index] = { ...newArray[index], [eventName]: false }
      }
    }
    setAttachmentsList(newArray)
  }

  const clearAttachmentTitleHandler = index => {
    const newArray = cloneDeep(attachmentsList)
    newArray[index] = { ...newArray[index], title: '' }
    setAttachmentsList(newArray)
  }
  // if changes in attachments number_of_attachments
  // update attachmentsList
  useEffect(() => {
    if (attachmentsList.length === numberOfAttachments) return
    // remove extra items
    if (attachmentsList.length > numberOfAttachments) {
      setAttachmentsList(prevList => {
        return prevList.slice(0, numberOfAttachments)
      })
      return
    }
    // add extra items
    if (attachmentsList.length < numberOfAttachments) {
      setAttachmentsList(prevList => {
        while (prevList.length < numberOfAttachments) {
          prevList.push(attachmentsObjectTemplate)
        }
        return prevList
      })
    }
  }, [numberOfAttachments])

  useEffect(() => {
    // if attachments list is updated
    // use debouncing so process for updating the whole object
    // only executes once if multiple keystrokes
    if (attachmentsList?.length && changes) {
      const timeoutId = setTimeout(updateAttachmentListInSelectedItem, 500)
      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [attachmentsList])

  const updateAttachmentListInSelectedItem = () => {
    if (selectedItem) {
      const newObject = cloneDeep(selectedItem)
      newObject[selectedItemKey].attachments = attachmentsList
      let required = false
      attachmentsList.forEach(attachment => {
        if (attachment.required) {
          required = true
        }
      })
      newObject[selectedItemKey].required = required
      setSelectedItem(newObject)
    }
  }
  // Attachment element actions end

  // Multi-Button Section actions start
  const changeButtonValueHandler = (event, index) => {
    if (!changes) setChanges(true)
    const newArray = cloneDeep(buttonsList)
    newArray[index] = event.target.value
    setButtonsList(newArray)
  }
  // update buttons list
  useEffect(() => {
    if (buttonsList.length === numberOfButtons) return
    // remove extra items
    if (buttonsList.length > numberOfButtons) {
      setButtonsList(prevList => {
        return prevList.slice(0, numberOfButtons)
      })
      return
    }
    // add extra items
    if (buttonsList.length < numberOfButtons) {
      setButtonsList(prevList => {
        while (prevList.length < numberOfButtons) {
          prevList.push('')
        }
        return prevList
      })
    }
  }, [numberOfButtons])

  useEffect(() => {
    // if photos list is updated
    // use debouncing so proces for updating the whole object
    // only executes once if multiple keystrokes
    if (buttonsList?.length) {
      const timeoutId = setTimeout(updateButtonsListInSelectedItem, 500)
      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [buttonsList])

  const updateButtonsListInSelectedItem = () => {
    if (selectedItem) {
      const newObject = cloneDeep(selectedItem)
      newObject[selectedItemKey].buttons = buttonsList
      setSelectedItem(newObject)
    }
  }

  // Multi-Button Section actions end

  // todo: separate each item into a different component file to avoid conflicts between elements
  const renderItemForm = () => {
    const item = selectedItem[selectedItemKey]
    const fields = []
    if (
      selectedItem?.[selectedItemKey]?.type === elementTypes.AUTHORIZATION_FIELD
    ) {
      fields.push(
        <Fragment>
          <Typography className={classes.itemsInputFieldMessage}>
            {t('form_builder.elements.authorization_field_message_one')}
          </Typography>
          <Typography className={classes.itemsInputFieldMessage}>
            {t('form_builder.elements.authorization_field_message_two')}
          </Typography>
        </Fragment>
      )
    }
    for (const [itemKey, value] of Object.entries(item)) {
      // has to  be required field
      // show error is
      const requiredField = mandatoryFields.includes(itemKey)
      const showError = requiredField && !value && selectedMoreThanOne
      switch (itemKey) {
        case elementKeys.TITLE:
          if (
            [
              elementTypes.MATERIAL_PARTS,
              elementTypes.AUTHORIZATION_FIELD
            ].includes(selectedItem?.[selectedItemKey]?.type)
          ) {
            break
          }
          if (
            selectedItem?.[selectedItemKey]?.type === elementTypes.LINE_ITEMS
          ) {
            fields.push(
              <Fragment>
                <Typography className={classes.itemsInputFieldMessage}>
                  {t('form_builder.elements.line_items_message_one')}
                </Typography>
                <Typography className={classes.itemsInputFieldMessage}>
                  {t('form_builder.elements.line_items_message_two')}
                </Typography>
              </Fragment>
            )
          }
          fields.push(
            <TextBox
              small={
                selectedItem[selectedItemKey].type === elementTypes.PHOTOS ||
                selectedItem[selectedItemKey].type === elementTypes.ATTACHMENTS
              }
              key={itemKey}
              name={itemKey}
              required={requiredField}
              value={value}
              onChange={changeFieldHandler}
              title={t('form_builder.elements.title_title')}
              placeholder={t('form_builder.elements.text_input_placeholder')}
              error={showError}
              disabled={disableItems}
            />
          )
          break
        case elementKeys.HELPFUL_HINT:
          fields.push(
            <TextBox
              key={itemKey}
              name={itemKey}
              value={value}
              onChange={changeFieldHandler}
              title={t('form_builder.elements.helpful_hint_title')}
              placeholder={t('form_builder.elements.helpful_hint_placeholder')}
              disabled={disableItems}
            />
          )
          break
        case elementKeys.TIME_STAMP:
          fields.push(
            <CustomSelect
              key={itemKey}
              name={itemKey}
              value={
                item?.time_stamp
                  ? t('form_builder.elements.yes')
                  : t('form_builder.elements.no')
              }
              onChange={changeFieldHandler}
              title={t('form_builder.elements.time_stamp_title')}
              options={[
                t('form_builder.elements.yes'),
                t('form_builder.elements.no')
              ]}
              disabled={disableItems}
            />
          )
          break
        case elementKeys.REQUIRED: {
          if (
            selectedItem?.[selectedItemKey]?.type ===
            elementTypes.AUTHORIZATION_FIELD
          ) {
            fields.push(
              <CustomSelect
                nonSortedOptions={true}
                key={itemKey}
                name={itemKey}
                value={t(`form_builder.elements.${value}`)}
                onChange={changeFieldHandler}
                title={t('form_builder.elements.required_field_title')}
                options={requiredAuthorizationOptions?.map(item =>
                  t(`form_builder.elements.${item}`)
                )}
                disabled={disableItems}
              />
            )
            break
          }
          if (
            selectedItem?.[selectedItemKey]?.type !== elementTypes.PHOTOS ||
            selectedItem?.[selectedItemKey]?.type !== elementTypes.ATTACHMENTS
          ) {
            let trueLabel = t('form_builder.right_panel.required')
            if (
              selectedItem?.[selectedItemKey]?.type === elementTypes.LINE_ITEMS
            ) {
              trueLabel =
                trueLabel +
                ' ' +
                `(${t('form_builder.elements.one_line_item')})`
            }
            fields.push(
              <CustomSelect
                key={itemKey}
                name={itemKey}
                value={
                  value ? trueLabel : t('form_builder.right_panel.optional')
                }
                onChange={changeFieldHandler}
                title={t('form_builder.elements.required_field_title')}
                options={[trueLabel, t('form_builder.right_panel.optional')]}
                disabled={disableItems}
              />
            )
          }
          break
        }
        case elementKeys.EMPTY_STATE_MESSAGE:
          fields.push(
            <TextBox
              small
              key={itemKey}
              name={itemKey}
              value={value}
              onChange={changeFieldHandler}
              title={t('form_builder.elements.empty_state_message_title')}
              onClear={clearFieldHandler}
              placeholder={t(
                'form_builder.elements.empty_state_message_placeholder'
              )}
              disabled={disableItems}
            />
          )
          break
        case elementKeys.INPUT_FIELD_TYPE:
          fields.push(
            <CustomSelect
              nonSortedOptions={true}
              key={itemKey}
              name={itemKey}
              value={t(`form_builder.elements.${value}`)}
              onChange={changeFieldHandler}
              title={t('form_builder.elements.input_field_type_title')}
              options={inputFieldTypeOptions?.map(item =>
                t(`form_builder.elements.${item}`)
              )}
              disabled={disableItems}
            />
          )
          break
        case elementKeys.SELECTION_OPTION:
          fields.push(
            <CustomSelect
              key={itemKey}
              name={itemKey}
              value={t(`form_builder.elements.${value}`)}
              onChange={changeFieldHandler}
              title={t('form_builder.elements.selection_option')}
              options={selectionOptionOptions?.map(item =>
                t(`form_builder.elements.${item}`)
              )}
              disabled={disableItems}
            />
          )
          break
        case elementKeys.SELECTION_TYPE:
          fields.push(
            <CustomSelect
              key={itemKey}
              name={itemKey}
              value={t(`form_builder.elements.${value}`)}
              onChange={changeFieldHandler}
              title={t('form_builder.elements.selection_type')}
              options={selectionTypeOptions?.map(item =>
                t(`form_builder.elements.${item}`)
              )}
              disabled={disableItems}
            />
          )
          break
        case elementKeys.NUMBER_OF_PHOTOS:
          fields.push(
            <PhotosListComponent
              onClearTitle={clearPhotoTitleHandler}
              key={elementKeys.NUMBER_OF_PHOTOS}
              itemKey={elementKeys.NUMBER_OF_PHOTOS}
              name={elementKeys.NUMBER_OF_PHOTOS}
              numberOfPhotos={numberOfPhotos}
              onChangeNumberOfPhotos={changeFieldHandler}
              maximumNumberOfPhotos={maximumNumberOfPhotos}
              disableItems={disableItems}
              photosList={photosList}
              onChangeItemValue={changePhotosListItemHandler}
              selectedMoreThanOne={selectedMoreThanOne}
            />
          )
          break
        case elementKeys.NUMBER_OF_ATTACHMENTS:
          fields.push(
            <AttachmentsListComponent
              onClearTitle={clearAttachmentTitleHandler}
              key={elementKeys.NUMBER_OF_ATTACHMENTS}
              itemKey={elementKeys.NUMBER_OF_ATTACHMENTS}
              name={elementKeys.NUMBER_OF_ATTACHMENTS}
              numberOfAttachments={numberOfAttachments}
              onChangeNumberOfAttachments={changeFieldHandler}
              maximumNumberOfAttachments={maximumNumberOfAttachments}
              disableItems={disableItems}
              attachmentsList={attachmentsList}
              onChangeItemValue={changeAttachmentsListItemHandler}
              selectedMoreThanOne={selectedMoreThanOne}
            />
          )
          break
        case elementKeys.OPTIONS: {
          const currentType = selectedItem?.[selectedItemKey]?.type
          const currentRate = selectedItem?.[selectedItemKey]?.rate

          const isRateBasedItem = currentType === elementTypes.LINE_ITEMS

          const showForRateBased =
            isRateBasedItem &&
            currentRate?.trim() === rateOptionsObj.ENABLE_CUSTOM_OPTIONS

          // Condition B: It's a standard dropdown that always requires options
          const showForDropdown =
            currentType === elementTypes.DROPDOWN_SELECTION

          if (showForRateBased || showForDropdown) {
            fields.push(
              <SelectionComponent
                disableItems={disableItems}
                key={elementKeys.OPTIONS}
                selectionOptions={selectionOptions}
                variant="outlined"
                label={t('form_builder.elements.selection_button')}
                onAddSelection={addSelectionHandler}
                onRemoveSelection={removeSelectionHandler}
                onChangeOptionText={changeOptionTextHandler}
                selectedMoreThanOne={selectedMoreThanOne}
              />
            )
          }
          break
        }

        case elementKeys.NUMBER_OF_BUTTONS:
          fields.push(
            <ButtonsListComponent
              selectedMoreThanOne={selectedMoreThanOne}
              itemData={item}
              numberOfButtons={numberOfButtons}
              onChangeSelectionsField={changeFieldHandler}
              onChangeButtonValue={changeButtonValueHandler}
              maximumNumberOfButtons={maximumNumberOfButtons}
              disableItems={disableItems}
              numberOfSelectionsOptions={selectionTypeOptions}
              buttonsList={buttonsList}
            />
          )
          break
        case elementKeys.ATTRIBUTES:
          fields.push(
            <CustomSelect
              key={itemKey}
              name={itemKey}
              value={t(`form_builder.elements.${value}`)}
              onChange={changeFieldHandler}
              title={t('form_builder.elements.attributes')}
              options={signatureAttributesOptions?.map(item =>
                t(`form_builder.elements.${item}`)
              )}
              disabled={disableItems}
            />
          )
          break
        case elementKeys.ITEM_PRICE:
          fields.push(
            <CustomSelect
              key={itemKey}
              name={itemKey}
              value={t(`form_builder.elements.${value}`)}
              onChange={changeFieldHandler}
              title={t('form_builder.elements.item_price')}
              options={itemPriceOptions?.map(item =>
                t(`form_builder.elements.${item}`)
              )}
              disabled={disableItems}
            />
          )
          break
        case elementKeys.PRODUCT_CREATION:
          fields.push(
            <CustomSelect
              key={itemKey}
              name={itemKey}
              value={
                item?.product_creation
                  ? t('form_builder.elements.enable')
                  : t('form_builder.elements.disable')
              }
              onChange={changeFieldHandler}
              title={t(`form_builder.elements.${itemKey}`)}
              options={[
                t('form_builder.elements.enable'),
                t('form_builder.elements.disable')
              ]}
              disabled={disableItems}
            />
          )
          break
        case elementKeys.PRODUCT_NAME:
        case elementKeys.PRODUCT_DESCRIPTION:
          {
            const titleLabel = t(`form_builder.elements.${itemKey}`)
            fields.push(
              <CustomSelect
                key={itemKey}
                name={itemKey}
                value={t(`form_builder.elements.${value}`)}
                onChange={changeFieldHandler}
                title={titleLabel}
                options={lineItemsFieldsOptions
                  ?.map(item => t(`form_builder.elements.${item}`))
                  .filter(item => item !== t('form_builder.elements.hidden'))}
                disabled={disableItems}
              />
            )
          }
          break
        case elementKeys.PRICE_EACH:
          {
            const titleLabel = t(`form_builder.elements.${itemKey}`)
            let disablePrice = disableItems
            if (item.product_creation) {
              disablePrice = true
            }
            fields.push(
              <CustomSelect
                key={itemKey}
                name={itemKey}
                value={t(`form_builder.elements.${value}`)}
                onChange={changeFieldHandler}
                title={titleLabel}
                options={lineItemsFieldsOptions?.map(item =>
                  t(`form_builder.elements.${item}`)
                )}
                disabled={disablePrice}
              />
            )
          }
          break
        case elementKeys.TAX:
          {
            const titleLabel = t(`form_builder.elements.${itemKey}`)
            fields.push(
              <CustomSelect
                key={itemKey}
                name={itemKey}
                value={t(`form_builder.elements.${value}`)}
                onChange={changeFieldHandler}
                title={titleLabel}
                options={lineItemsFieldsOptions?.map(item =>
                  t(`form_builder.elements.${item}`)
                )}
                disabled={disableItems}
              />
            )
          }
          break
        case elementKeys.TOTAL_EACH:
          {
            const titleLabel = t(`form_builder.elements.${itemKey}`)
            fields.push(
              <CustomSelect
                key={itemKey}
                name={itemKey}
                value={t(`form_builder.elements.${value}`)}
                onChange={changeFieldHandler}
                title={titleLabel}
                options={lineItemsFieldsOptions
                  ?.map(item => t(`form_builder.elements.${item}`))
                  .filter(item => item !== t('form_builder.elements.editable'))}
                disabled={disableItems}
              />
            )
          }
          break
        case elementKeys.DESCRIPTION: {
          let disablePicker = disableItems
          if (
            !disableItems &&
            [elementTypes.MATERIAL_PARTS].includes(
              selectedItem?.[selectedItemKey]?.type
            ) &&
            extraConditions.materialPartsTotalAmountDue &&
            !value
          ) {
            disablePicker = true
          }

          let titleLabel = t(`form_builder.elements.${itemKey}`)
          if (
            selectedItem?.[selectedItemKey]?.type === elementTypes.LINE_ITEMS &&
            itemKey === elementKeys.DESCRIPTION
          ) {
            titleLabel = t('form_builder.elements.labor_description')
          }
          fields.push(
            <CustomSelect
              key={itemKey}
              name={itemKey}
              value={
                value
                  ? t('form_builder.right_panel.enable')
                  : t('form_builder.right_panel.disable')
              }
              onChange={changeFieldHandler}
              title={titleLabel}
              options={[
                t('form_builder.right_panel.enable'),
                t('form_builder.right_panel.disable')
              ]}
              disabled={disablePicker}
            />
          )
          break
        }
        case elementKeys.RATE:
          fields.push(
            <CustomSelect
              key={itemKey}
              name={itemKey}
              value={t(`form_builder.elements.${value}`)}
              onChange={changeFieldHandler}
              title={t('form_builder.elements.rate')}
              options={rateOptions?.map(item =>
                t(`form_builder.elements.${item}`)
              )}
              disabled={disableItems}
            />
          )
          break
        case elementKeys.PAYMENT_TERMS: {
          const paymentTermsTitleLabel =
            t('form_builder.elements.payment_terms') +
            ' ' +
            '/' +
            ' ' +
            t('form_builder.elements.due_date')
          fields.push(
            <CustomSelect
              key={itemKey}
              name={itemKey}
              value={t(`form_builder.elements.${value}`)}
              onChange={changeFieldHandler}
              title={paymentTermsTitleLabel}
              options={paymentTermsOptions?.map(item =>
                t(`form_builder.elements.${item}`)
              )}
              disabled={disableItems}
            />
          )
          break
        }
        default:
          break
      }
    }

    // if nested logic is enable for that item
    // and element has no dependency
    if (elementKeys.NESTED_LOGIC in item && !(elementKeys.DEPENDENCY in item)) {
      fields.push(<Divider className={classes.dividerAlt} />)
      // nested_logic
      const comingSoon = elementTypes.SIGNATURE === item.type
      fields.push(
        <CustomSelect
          disabled={disableItems || comingSoon}
          key={elementKeys.NESTED_LOGIC}
          name={elementKeys.NESTED_LOGIC}
          value={
            comingSoon
              ? t('form_builder.elements.coming_soon')
              : item?.nested_logic
                ? t('form_builder.elements.yes')
                : t('form_builder.elements.none')
          }
          onChange={changeFieldHandler}
          title={t('form_builder.elements.nested_logic')}
          options={[
            t('form_builder.elements.yes'),
            t('form_builder.elements.none')
          ]}
        />
      )
      // if nested logic true
      if (item?.nested_logic) {
        // set logical actions and value options depending on item type start
        let logicalActionOptions = []
        let logicalActionValue = ''
        switch (item.type) {
          case elementTypes.DROPDOWN_SELECTION:
            logicalActionOptions = selectionOptions?.map((_, idx) =>
              t('form_builder.elements.dropdown_selection_option', {
                option: idx + 1
              })
            )
            logicalActionValue = t(
              'form_builder.elements.dropdown_selection_option',
              {
                option: item?.logical_action
              }
            )
            break
          case elementTypes.MATERIAL_PARTS:
            logicalActionOptions = materialsLogicalActionOptions?.map(
              option => {
                return t(`form_builder.elements.${option}`)
              }
            )
            logicalActionValue = t(
              `form_builder.elements.${item?.logical_action}`
            )
            break
          case elementTypes.LINE_ITEMS:
            logicalActionOptions = lineItemsLogicalActionOptions?.map(
              option => {
                return t(`form_builder.elements.${option}`)
              }
            )
            logicalActionValue = t(
              `form_builder.elements.${item?.logical_action}`
            )
            break
          case elementTypes.MULTI_BUTTON_SELECTION:
            logicalActionOptions = buttonsList?.map((_, idx) =>
              t('form_builder.elements.button_selection_option', {
                option: idx + 1
              })
            )
            logicalActionValue = t(
              'form_builder.elements.button_selection_option',
              {
                option: item?.logical_action
              }
            )
            break
          case elementTypes.TEXT_DESCRIPTION:
            logicalActionOptions = ['if_text_is_populated']?.map(option =>
              t(`form_builder.elements.${option}`)
            )
            logicalActionValue = t(
              `form_builder.elements.${item?.logical_action}`
            )
            break
          case elementTypes.CHECKLIST_ITEM:
            logicalActionOptions = [
              'if_check_mark_is_selected',
              'if_red_x_is_selected'
            ]?.map(option => t(`form_builder.elements.${option}`))
            logicalActionValue = item?.logical_action
              ? t('form_builder.elements.if_check_mark_is_selected')
              : t('form_builder.elements.if_red_x_is_selected')
            break
          default:
            break
        }
        // set logical actions and value options depending on item type end

        // logical action
        fields.push(
          <CustomSelect
            disabled={disableItems}
            key={elementKeys.LOGICAL_ACTION}
            name={elementKeys.LOGICAL_ACTION}
            value={logicalActionValue}
            onChange={changeFieldHandler}
            title={t('form_builder.elements.logical_action')}
            options={logicalActionOptions}
          />
        )
        // then perform
        const thenPerformFiltered = thenPerformOptions.filter(
          x => x !== 'equipment_inventory'
        )
        fields.push(
          <CustomSelect
            disabled={disableItems}
            key={elementKeys.THEN_PERFORM}
            name={elementKeys.THEN_PERFORM}
            value={
              item?.then_perform &&
              t(`form_builder.elements.${item?.then_perform}`)
            }
            onChange={changeFieldHandler}
            title={t('form_builder.elements.then_perform')}
            options={thenPerformFiltered?.map(option =>
              t(`form_builder.elements.${option}`)
            )}
            placeholder={t('form_builder.elements.then_perform_placeholder')}
          />
        )
      }
    }
    // Set the field with key='number_of_attachments' to the end of the fields array
    const numberOfAttachmentsFields = fields.find(
      field => field.key === elementKeys.NUMBER_OF_ATTACHMENTS
    )
    if (numberOfAttachmentsFields) {
      fields.splice(fields.indexOf(numberOfAttachmentsFields), 1)
      fields.push(numberOfAttachmentsFields)
    }
    // Set the field with key='number_of_photos' to the end of the fields array
    const numberOfPhotosFields = fields.find(
      field => field.key === elementKeys.NUMBER_OF_PHOTOS
    )
    if (numberOfPhotosFields) {
      fields.splice(fields.indexOf(numberOfPhotosFields), 1)
      fields.push(numberOfPhotosFields)
    }

    return fields
  }

  const actionButtons = () => {
    const disableClone =
      missingValuesCounter > 0 ||
      elementKeys.DEPENDENCY in
        selectedItem[getFirstKeyFromObject(selectedItem)] ||
      statusOptions.PUBLISHED === status

    const disableDelete = statusOptions.PUBLISHED === status
    return (
      <Box className={classes.actionIconsConatiner}>
        <IconButton
          disableRipple
          onClick={onCloneElement}
          disabled={disableClone}
        >
          <Box>
            <FontAwesomeIcon
              className={
                disableClone ? classes.actionIconsDisabled : classes.actionIcons
              }
              icon={faClone}
            />
            <Typography
              className={`${classes.actionText} ${
                disableClone && classes.actionDisabled
              }`}
            >
              {t('form_builder.clone')}
            </Typography>
          </Box>
        </IconButton>
        <IconButton
          disableRipple
          onClick={() => onDeleteElement(selectedItemKey)}
          disabled={disableDelete}
        >
          <Box>
            <FontAwesomeIcon
              className={
                disableDelete
                  ? classes.actionIconsDisabled
                  : classes.actionIcons
              }
              icon={faTrash}
            />
            <Typography
              className={`${classes.actionText} ${
                disableDelete && classes.actionDisabled
              }`}
            >
              {t('form_builder.delete')}
            </Typography>
          </Box>
        </IconButton>
      </Box>
    )
  }

  // main component
  return (
    <Box className={classes.rightPanelContainer}>
      {!!selectedItem && !emptySections && (
        <Container className={classes.secondaryContainer}>
          <Box className={classes.itemNameConatiner}>
            <Typography className={classes.ItemName}>
              {t(
                `form_builder.elements.${selectedItem[selectedItemKey]?.type}`
              )}
            </Typography>
            {actionButtons()}
          </Box>
          <Box className={classes.contentContainer}>
            {renderItemForm()}
            <Box className={classes.bottomSpace} />
          </Box>
        </Container>
      )}
      {emptySections && emptyMessage()}
      {!emptySections && !selectedItem && continueMessage()}
    </Box>
  )
}

export default RightPanel
